import type { Punto, Soglie } from "./types.ts";

/**
 * Logica pura della scheda Risorse: dati nel formato di uPlot, finestre
 * temporali e stato della pagina nell'URL. Separata dal componente per poterla
 * verificare senza un browser.
 */

export type Metrica = "cpu" | "memoria" | "rete";

/** Il valore mostrato: i picchi, perché la scheda serve a trovare chi ha saturato. */
function valore(p: Punto, metrica: Metrica): number | null {
  if (metrica === "cpu") return p.cpuMax;
  if (metrica === "memoria") return p.memPctMax;
  if (p.rxMax === null && p.txMax === null) return null;
  return ((p.rxMax ?? 0) + (p.txMax ?? 0)) / 1024;
}

/**
 * Le serie su un unico asse dei tempi, come lo vuole uPlot: `[x, ...y]`.
 *
 * Un `null` interrompe la linea. Dove una serie salta più di due secchi (il
 * container era fermo) si inserisce un istante vuoto se nessun'altra serie ne
 * ha uno lì in mezzo: altrimenti uPlot unirebbe i due tratti come se il
 * servizio fosse rimasto vivo. Un secchio saltato da tutti, invece, resta unito.
 */
export function allineaSerie(
  serie: Record<string, Punto[]>,
  secchio: number,
  metrica: Metrica,
): { nomi: string[]; dati: (number | null)[][] } {
  const nomi = Object.keys(serie).sort();
  const istanti = new Set(nomi.flatMap((n) => serie[n]!.map((p) => p.ts)));
  const ordinati = () => [...istanti].sort((x, y) => x - y);

  const comuni = ordinati();
  for (const n of nomi) {
    const punti = serie[n]!;
    for (let i = 1; i < punti.length; i++) {
      const [p, q] = [punti[i - 1]!.ts, punti[i]!.ts];
      if (q - p > 2 * secchio && !comuni.some((t) => t > p && t < q)) istanti.add(p + secchio);
    }
  }

  const xs = ordinati();
  const ys = nomi.map((n) => {
    const perIstante = new Map(serie[n]!.map((p) => [p.ts, valore(p, metrica)]));
    return xs.map((t) => perIstante.get(t) ?? null);
  });

  return { nomi, dati: [xs, ...ys] };
}

export type SceltaRapida = "1h" | "6h" | "24h" | "7g";

const DURATE: Record<SceltaRapida, number> = { "1h": 3600, "6h": 6 * 3600, "24h": 86400, "7g": 7 * 86400 };

/** La finestra che finisce a `ora` (secondi epoch). */
export function finestraRapida(scelta: SceltaRapida, ora: number): { da: number; a: number } {
  return { da: ora - DURATE[scelta], a: ora };
}

export type Scheda = "panoramica" | "risorse" | "configurazione" | "rilascio" | "storico";

export const SCHEDE: readonly Scheda[] = ["panoramica", "risorse", "configurazione", "rilascio", "storico"];

/** I nomi delle schede di prima, che restano nei link delle email e nei segnalibri. */
const NOMI: Record<string, Scheda> = {
  ...Object.fromEntries(SCHEDE.map((s) => [s, s])),
  container: "panoramica",
  verifica: "rilascio",
};

/** Le sotto-schede di Rilascio: una alla volta, così nessuna resta nascosta sotto la piega. */
export type SezioneRilascio = "controlli" | "strategia" | "instradamenti" | "automazione";

export const SEZIONI_RILASCIO: readonly SezioneRilascio[] = ["controlli", "strategia", "instradamenti", "automazione"];

export interface StatoPagina {
  /** Assente: la pagina apre `schedaPredefinita`. */
  scheda?: Scheda;
  /** Assente: i controlli, la prima cosa da guardare prima di avviare. */
  sezione?: SezioneRilascio;
  da?: number;
  a?: number;
  servizio?: string;
}

/**
 * Lo stato della pagina letto dall'URL: il link di un'email di avviso apre la
 * scheda già posizionata su finestra e servizio.
 */
export function leggiStato(search: string): StatoPagina {
  const q = new URLSearchParams(search);
  const stato: StatoPagina = {};
  const scheda = Object.hasOwn(NOMI, q.get("scheda") ?? "") ? NOMI[q.get("scheda")!] : undefined;
  const intero = (v: string | null) => (v !== null && /^\d+$/.test(v) ? Number(v) : undefined);
  const da = intero(q.get("da"));
  const a = intero(q.get("a"));
  const servizio = q.get("servizio");
  const sezione = SEZIONI_RILASCIO.find((s) => s === q.get("sezione"));
  if (scheda) stato.scheda = scheda;
  if (sezione) stato.sezione = sezione;
  if (da !== undefined) stato.da = da;
  if (a !== undefined) stato.a = a;
  if (servizio) stato.servizio = servizio;

  return stato;
}

/** L'inverso di `leggiStato`; senza scheda l'indirizzo resta pulito. */
export function scriviStato(s: StatoPagina): string {
  const q = new URLSearchParams();
  if (s.scheda) q.set("scheda", s.scheda);
  if (s.sezione) q.set("sezione", s.sezione);
  if (s.da !== undefined) q.set("da", String(s.da));
  if (s.a !== undefined) q.set("a", String(s.a));
  if (s.servizio) q.set("servizio", s.servizio);
  const testo = q.toString();

  return testo ? `?${testo}` : "";
}

/**
 * Colori delle serie, uno per servizio nell'ordine di `allineaSerie`.
 *
 * Il tema di Portman ha una sola tinta d'accento, quindi serve una tavolozza
 * categorica: tinte verificate per daltonismo, con un passo diverso per lo
 * sfondo scuro. Il rosso della tavolozza è tolto apposta: è riservato ai
 * marcatori di OOM e arresti, e un servizio dello stesso colore li
 * confonderebbe. Oltre il settimo servizio i colori si ripetono; la tabella
 * sotto i grafici resta comunque la legenda, col nome accanto a ogni colore.
 */
const COLORI = {
  chiaro: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6"],
  scuro: ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9"],
};

/** Colore dei marcatori di OOM e arresti inattesi, fuori dalla tavolozza delle serie. */
export const coloreMarcatore = (scuro: boolean): string => (scuro ? "#e66767" : "#e34948");

export const coloreSerie = (indice: number, scuro: boolean): string => {
  const tavolozza = scuro ? COLORI.scuro : COLORI.chiaro;
  return tavolozza[indice % tavolozza.length]!;
};

/**
 * La tabella del live: il valore dell'ultimo passo di ogni servizio, perché il
 * live serve a sapere come sta adesso (i picchi li mostrano i grafici). Ordine
 * per nome, così le righe non saltano a ogni aggiornamento; eventi non ne arrivano.
 */
export function riepilogoLive(serie: Record<string, Punto[]>, soglie: Soglie) {
  return Object.keys(serie)
    .sort()
    .map((nome) => {
      const p = serie[nome]!.at(-1)!;

      return {
        serie: nome,
        cpuMax: p.cpuMax,
        memPctMax: p.memPctMax,
        rxMax: p.rxMax,
        txMax: p.txMax,
        eventi: 0,
        sopraSoglia: (p.cpuMax ?? 0) > soglie.cpuPct || p.memPctMax > soglie.memPct,
      };
    });
}
